import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import { getWisdomLampBanner, getLampSchedule, getLampRegionList, getLightingPostSummaryList, getBehindScenesList } from '../api/lampService';
import { parsePeriodList } from '../utils/scheduleUtils';
import { usePageLayout } from '@app/router';
import { ROUTES } from '@app/router/routes';
import { isYouTubeUrl, getYouTubeThumbnail, getYouTubeEmbedUrl } from '@shared/utils/videoUtils';
import CloseButton from '@/shared/components/CloseButton';
import { useLanguage } from '@shared/contexts/LanguageContext';

const LINE = { borderLeft: '1px solid #6E625B' };
const LINE_PL = { ...LINE, paddingLeft: 20 };
const ACCENT = { borderLeft: '6px solid #BC313D', paddingLeft: 20 };

const PostVideoCard = ({ post, onClick, expanded }) => {
  const videoUrl = post.video?.[0]?.cache_url;
  const thumbnailUrl = post.video?.[0]?.thumbnail?.cache_url;
  const isYT = videoUrl && isYouTubeUrl(videoUrl);
  const effectiveThumbnail = thumbnailUrl || (isYT ? getYouTubeThumbnail(videoUrl) : null);
  const size = expanded ? '100%' : 155;
  return (
    <div
      className="cursor-pointer group"
      style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, ...(expanded ? {} : { width: 155, flexShrink: 0 }) }}
      onClick={() => videoUrl && onClick()}
    >
      <div style={{ width: size, aspectRatio: '1', position: 'relative', background: '#FAF7F3', boxShadow: '2px 4px 8px rgba(110,98,91,0.08)', borderRadius: 16, overflow: 'hidden' }}>
        {effectiveThumbnail ? (
          <img src={effectiveThumbnail} alt={post.monastery_name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : videoUrl ? (
          <video src={`${videoUrl}#t=0.1`} preload="metadata" muted playsInline controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(188,49,61,0.08)' }}>
            <span style={{ fontFamily: 'Noto Serif TC', fontSize: 20, fontWeight: 700, color: 'rgba(188,49,61,0.35)' }}>{post.monastery_name?.charAt(0) || '舍'}</span>
          </div>
        )}
        {videoUrl && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
            <span className="material-symbols-outlined" style={{ color: 'rgba(255,255,255,0.85)', fontSize: expanded ? 40 : 32 }}>play_arrow</span>
          </div>
        )}
      </div>
      <p className="line-clamp-2 text-center" style={{ fontFamily: 'Noto Serif TC', fontSize: 14, fontWeight: 700, color: '#6E625B', letterSpacing: '0.1em', width: '100%' }}>
        {post.monastery_name}
      </p>
    </div>
  );
};

/**
 * 傳燈活動主頁
 */
const WisdomLampPage = () => {
  const { setLayoutOptions } = usePageLayout();
  const navigate = useNavigate();
  const { t, currentLanguage } = useLanguage();
  const isEn = currentLanguage.code === 'en';
  const [scheduleItems, setScheduleItems] = useState([]);
  const [mapItems, setMapItems] = useState([]);
  const [branchRegions, setBranchRegions] = useState([]);
  const [scheduleTab, setScheduleTab] = useState('time');
  const [showAllSchedule, setShowAllSchedule] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [banner, setBanner] = useState(null);
  const [videoModal, setVideoModal] = useState(false);
  const [activeMapIndex, setActiveMapIndex] = useState(0);
  const mapSwiperRef = useRef(null);
  const [behindScenes, setBehindScenes] = useState([]);
  const [selectedScene, setSelectedScene] = useState(null);
  const [posts, setPosts] = useState([]);         // 當週/最近週，初始顯示
  const [allPosts, setAllPosts] = useState([]);    // 全部，查看更多後使用
  const [selectedPost, setSelectedPost] = useState(null);
  const POSTS_INITIAL = 6;
  const POSTS_STEP = 4;
  const [postsVisible, setPostsVisible] = useState(POSTS_INITIAL);
  // const postListRef = useRef(null);           // 紅點指示器用
  // const [activePostIndex, setActivePostIndex] = useState(0); // 紅點指示器用
  const timetableRef = useRef(null);
  const [monasteryPopup, setMonasteryPopup] = useState(null);  // { id, name, address, start_at, has_lighting_post }
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768);
  useEffect(() => {
    const handler = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // 從 branchRegions 找精舍的 address / start_at
  const findMonasteryDetail = (id) => {
    for (const region of branchRegions) {
      for (const child of (region.children || [])) {
        const items = child.type === 'sub_region' ? (child.children || []) : [child];
        for (const m of items) {
          if (m.id === id) return m;
        }
      }
    }
    return null;
  };

  const handleCloseVideo = () => {
    setVideoModal(false);
  };

  const handleJumpToCurrentWeek = () => {
    setScheduleTab('time');

    let closestMonth = null;
    let closestId = null;
    if (scheduleItems.length > 0) {
      const currentIdx = scheduleItems.findIndex((i) => i.status === 'current');
      const lastPastIdx = scheduleItems.reduce((acc, i, idx) => i.status === 'past' ? idx : acc, -1);
      const firstFutureIdx = scheduleItems.findIndex((i) => i.status === 'future');
      const closestIdx = currentIdx !== -1 ? currentIdx
        : lastPastIdx !== -1 ? lastPastIdx
          : firstFutureIdx !== -1 ? firstFutureIdx
            : scheduleItems.length - 1;
      const closest = scheduleItems[closestIdx];

      if (closest) {
        closestMonth = parseInt(closest.date.split('/')[0], 10);
        closestId = closest.lighting_id;
      }
    }

    if (closestMonth) {
      setSelectedMonth(closestMonth);
    } else {
      setShowAllSchedule(false);
    }

    setTimeout(() => {
      if (closestId) {
        const el = document.getElementById(`schedule-${closestId}`);
        if (el) {
          const headerOffset = 100;
          const elementPosition = el.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
          return;
        }
      }
      if (timetableRef.current) {
        const headerOffset = 80;
        const elementPosition = timetableRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    setLayoutOptions({ noPaddingBottom: true });
    return () => setLayoutOptions({});
  }, [setLayoutOptions]);

  useEffect(() => {
    getWisdomLampBanner()
      .then((res) => {
        const item = res.media?.[0];
        if (item) setBanner(item);
      })
      .catch((err) => console.error('Failed to fetch banner:', err));

    getLampSchedule({ show_video_only: true })
      .then((res) => {
        const items = parsePeriodList(res.items);
        // console.log('MapItems', items);
        setMapItems(items);
        const lastIdx = items.length - 1;
        setActiveMapIndex(lastIdx);
        if (mapSwiperRef.current) mapSwiperRef.current.slideTo(lastIdx, 0);
      })
      .catch((err) => console.error('Failed to fetch map schedule:', err));

    getLampSchedule()
      .then((res) => {
        const items = parsePeriodList(res.items);
        // console.log('ScheduleItems', items);
        setScheduleItems(items);
        const currentIdx = items.findIndex((i) => i.status === 'current');
        const lastPastIdx = items.reduce((acc, i, idx) => i.status === 'past' ? idx : acc, -1);
        const firstFutureIdx = items.findIndex((i) => i.status === 'future');
        const closestIdx = currentIdx !== -1 ? currentIdx
          : lastPastIdx !== -1 ? lastPastIdx
            : firstFutureIdx !== -1 ? firstFutureIdx
              : items.length - 1;
        const closest = items[closestIdx];
        if (closest) setSelectedMonth(parseInt(closest.date.split('/')[0], 10));
      })
      .catch((err) => console.error('Failed to fetch lamp schedule:', err));

    getLampRegionList()
      .then((res) => {
        const items = res.items || [];
        setBranchRegions(items);
        if (items.length > 0) setSelectedRegion(items[0].name);
      })
      .catch((err) => console.error('Failed to fetch region list:', err));

    getBehindScenesList()
      .then((res) => {
        setBehindScenes(res.items || []);
      })
      .catch((err) => console.error('Failed to fetch behind scenes:', err));

    getLightingPostSummaryList()
      .then((res) => {
        const all = res.items || [];
        setAllPosts(all);
        const now = new Date();
        const thisWeek = all.filter((p) => {
          if (!p.start_at || !p.end_at) return false;
          return now >= new Date(p.start_at) && now <= new Date(p.end_at);
        });
        if (thisWeek.length > 0) { setPosts(thisWeek); return; }
        const past = all.filter((p) => p.end_at && new Date(p.end_at) < now);
        if (past.length > 0) {
          const latestEnd = Math.max(...past.map((p) => new Date(p.end_at).getTime()));
          setPosts(past.filter((p) => new Date(p.end_at).getTime() === latestEnd));
          return;
        }
        setPosts(all);
      })
      .catch((err) => console.error('Failed to fetch posts:', err));
  }, [currentLanguage]);

  // 找離今天最近的 3 筆
  const getClosestThree = (items) => {
    const pivotIdx = items.findIndex((i) => i.status === 'current' || i.status === 'future');
    if (pivotIdx === -1) return items.slice(-3);
    const start = Math.max(0, pivotIdx - 1);
    return items.slice(start, start + 3);
  };
  const closestThree = getClosestThree(scheduleItems);

  // 從 date 字串（如 "4/06 - 4/12"）取月份數字
  const getItemMonth = (item) => parseInt(item.date.split('/')[0], 10);

  // 所有出現的月份（去重排序）
  const availableMonths = [...new Set(scheduleItems.map(getItemMonth))].sort((a, b) => a - b);

  // 依月份 filter
  const filteredByMonth = selectedMonth
    ? scheduleItems.filter((item) => getItemMonth(item) === selectedMonth)
    : scheduleItems;

  return (
    <div style={{ backgroundColor: '#FEFEFE' }}>
      {/* ── Banner ── */}
      <div className="relative w-full">
        <video
          className="w-full h-auto object-cover object-top"
          autoPlay
          loop
          muted
          playsInline
          controlsList="nodownload"
          onContextMenu={(e) => e.preventDefault()}
        >
          <source src="/video/wisdomlamp/banner_video.mp4" type="video/mp4" />
        </video>
        {/* 燈籠裝飾圖 — top: 100% 從 banner 底部開始，貼頁面右側 */}
        <img
          alt=""
          src="/image/wisdomlamp/banner_lamp.png"
          className="absolute right-0 pointer-events-none w-full md:w-1/2 top-[28%] md:top-[60%]"
          style={{ zIndex: 2 }}
        />

      </div>

      {/* 影片下方漸層銜接 */}
      <div className="relative w-full" style={{ height: 0 }}>
        <div style={{ position: 'absolute', top: -10, left: 0, width: '100%', height: 60, background: 'linear-gradient(to bottom, rgb(248,233,197), #FEFEFE)', zIndex: 0, pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: -20, left: 0, width: '100%', height: 50, backdropFilter: 'blur(3px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 1, pointerEvents: 'none' }} />
      </div>

      {/* Section 1：介紹文字 */}
      <div className="relative w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto" style={{ zIndex: 1 }}>
        <section className="px-6 pt-18 md:pt-25  pb-0">
          <p style={{ color: '#6E625B', fontSize: 20, fontFamily: 'Noto Sans TC', fontWeight: 400, lineHeight: '32px', letterSpacing: '0.38px', textAlign: 'left' }}>
            {banner?.details?.content || t('wisdom-lamp.introDefault')}
          </p>
        </section>
      </div>

      <div className="w-full max-w-[430px] md:w-4/5 md:max-w-none mx-auto">
        {/* ── Section 2：傳燈路線圖（border 線從標題起） ── */}
        <div className="flex justify-center px-6 mt-4 mb-0 md:mt-8 ml-auto ">
          <button
            onClick={handleJumpToCurrentWeek}
            className="flex items-center gap-1.5"
          >
            <span style={{ color: '#BC313D', fontSize: 16, fontFamily: 'Noto Serif TC', fontWeight: 500, textDecoration: 'underline', textUnderlineOffset: 4 }}>{t('wisdom-lamp.jumpToCurrentWeek')}</span>
            <span className="material-symbols-outlined text-lg" n style={{ color: '#BC313D' }}>arrow_forward</span>
          </button>
        </div>
        <section className="px-6">
          {/* 標題（紅色粗 border，線從這裡開始） */}
          <div className="mt-6 flex items-center" style={ACCENT}>
            <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
              <span style={{ color: '#171717' }}>{t('wisdom-lamp.mediaTitleBlack')}</span><br />
              <span style={{ color: '#BC313D' }}>{t('wisdom-lamp.routeMapTitleRed')}</span>
            </h2>
            <img
              src="/image/wisdomlamp/cloud_icon.png"
              alt="cloud icon"
              style={{ width: 80, height: 'auto', flexShrink: 0, marginLeft: 12 }}
            />
          </div>

          {/* 路線圖影片 */}
          <div style={{ ...LINE, paddingLeft: 26, paddingTop: 16, paddingBottom: 16 }}>
            <p style={{ color: '#6E625B', fontSize: 18, fontFamily: 'Noto Serif TC', fontWeight: 300, lineHeight: '22px', marginBottom: 20 }}>
              {t('wisdom-lamp.routeMapTagline')}
            </p>
            {mapItems.some(i => i.map_url) && (
              <>
                <style>{`
                                    .map-swiper-pagination {
                                        display: flex;
                                        align-items: center;
                                        justify-content: center;
                                        gap: 6px;
                                        padding-top: 12px;
                                        position: static !important;
                                    }
                                    .map-swiper-pagination .swiper-pagination-bullet {
                                        width: 8px;
                                        height: 8px;
                                        background: rgba(188, 49, 61, 0.3);
                                        border-radius: 50%;
                                        opacity: 1;
                                        margin: 0 !important;
                                        transition: all 0.3s ease;
                                        flex-shrink: 0;
                                    }
                                    .map-swiper-pagination .swiper-pagination-bullet-active {
                                        background: #BC313D;
                                        width: 8px;
                                        border-radius: 50%;
                                    }
                                `}</style>
                <Swiper
                  modules={[Pagination]}
                  pagination={{ el: '.map-swiper-pagination', clickable: true }}
                  initialSlide={activeMapIndex}
                  onSwiper={(swiper) => {
                    mapSwiperRef.current = swiper;
                    if (activeMapIndex !== 0) swiper.slideTo(activeMapIndex, 0);
                  }}
                  onSlideChange={(swiper) => setActiveMapIndex(swiper.activeIndex)}
                  className="w-full"
                >
                  {mapItems.map((item) => (
                    <SwiperSlide key={item.lighting_id}>
                      <div className="w-full">
                        <p style={{ color: '#6E625B', fontSize: 13, fontFamily: 'Noto Serif TC', fontWeight: 400, marginBottom: 8, letterSpacing: '0.05em' }}>
                          {item.date}
                        </p>
                        {item.map_url ? (
                          <div
                            className="relative cursor-pointer rounded-lg overflow-hidden"
                            onClick={() => setVideoModal(true)}
                          >
                            {item.map_thumbs ? (
                              <img
                                src={item.map_thumbs}
                                alt={t('wisdom-lamp.mapPreviewAlt')}
                                className="w-full rounded-lg"
                                style={{ aspectRatio: '16/9', objectFit: 'cover' }}
                              />
                            ) : (
                              <video
                                key={item.map_url}
                                className="w-full rounded-lg"
                                loop
                                muted
                                playsInline
                                controlsList="nodownload"
                                onContextMenu={(e) => e.preventDefault()}
                              >
                                <source src={item.map_url} type="video/mp4" />
                              </video>
                            )}
                            <div className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                              <div className="w-14 h-14 rounded-full flex items-center justify-center bg-white/30 backdrop-blur-sm">
                                <span className="material-symbols-outlined text-white text-4xl translate-x-0.5">play_arrow</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="w-full rounded-lg flex items-center justify-center" style={{ aspectRatio: '16/9', backgroundColor: 'rgba(188,49,61,0.06)' }}>
                            <span style={{ fontFamily: 'Noto Serif TC', fontSize: 14, color: 'rgba(188,49,61,0.4)' }}>{t('wisdom-lamp.comingSoon')}</span>
                          </div>
                        )}
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>
                <div className="map-swiper-pagination" />
              </>
            )}
          </div>
        </section>

        {/* ── Section 3：供燈影音報導 ── */}
        <section className="px-6 relative">
          <img
            src="/image/wisdomlamp/water_lamp.png"
            alt=""
            aria-hidden="true"
            className="absolute top-0 left-0 w-full md:hidden pointer-events-none"
            style={{ zIndex: 0 }}
          />
          <div className="relative" style={{ zIndex: 1 }}>
            <div className="h-14" style={LINE_PL} />

            {/* 標題 */}
            <div style={ACCENT}>
              <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
                <span style={{ color: '#171717' }}>{t('wisdom-lamp.mediaTitleBlack')}</span><br />
                <span style={{ color: '#BC313D' }}>{t('wisdom-lamp.mediaTitleRed')}</span>
              </h2>
            </div>

            {/* 影音卡片 */}
            <div style={{ ...LINE, paddingLeft: 26, paddingTop: 24, paddingBottom: 16 }}>
              {posts.length === 0 ? (
                <div className="w-full flex items-center justify-center py-8">
                  <span style={{ fontFamily: 'Noto Serif TC', fontSize: 16, fontWeight: 400, color: 'rgba(188,49,61,0.5)', letterSpacing: '0.2em' }}>{t('wisdom-lamp.comingSoon')}</span>
                </div>
              ) : postsVisible <= POSTS_INITIAL ? (
                // 初始：顯示當週/最近週前6個橫向滑動
                <div className="overflow-x-auto no-scrollbar" style={{ display: 'flex', gap: 12, marginRight: -24, paddingRight: 24 }}>
                  {posts.slice(0, POSTS_INITIAL).map((post) => (
                    <PostVideoCard key={post.post_id} post={post} onClick={() => setSelectedPost(post)} />
                  ))}
                </div>
              ) : (
                // 展開後：顯示全部 allPosts
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {allPosts.slice(0, postsVisible).map((post) => (
                    <PostVideoCard key={post.post_id} post={post} onClick={() => setSelectedPost(post)} expanded />
                  ))}
                </div>
              )}
              {/* 紅點指示器（保留備用）
              {posts.length > 1 && (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, paddingTop: 12 }}>
                  {posts.map((_, idx) => (
                    <div
                      key={idx}
                      style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: idx === activePostIndex ? '#BC313D' : 'rgba(188,49,61,0.3)',
                        transition: 'background 0.3s ease', flexShrink: 0,
                      }}
                    />
                  ))}
                </div>
              )}
              */}
              {(postsVisible <= POSTS_INITIAL ? allPosts.length > posts.length : postsVisible < allPosts.length) && (
                <div className="flex justify-center pt-4 pb-2">
                  <button
                    onClick={() => setPostsVisible((v) => v + POSTS_STEP)}
                    style={{ color: '#BC313D', fontSize: 16, fontFamily: 'Noto Serif TC', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
                  >
                    {t('wisdom-lamp.viewMore')}
                    <span className="material-symbols-outlined text-base">expand_more</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* ── Section 4：傳燈時程表 ── */}
        <section ref={timetableRef} className={`px-6 ${behindScenes.length === 0 ? 'pb-10' : ''}`}>
          <div className="h-20" style={LINE_PL} />

          {/* 標題 */}
          <div style={ACCENT}>
            <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
              <span style={{ color: '#171717' }}>{t('wisdom-lamp.scheduleTitleBlack')}</span><br />
              <span style={{ color: '#BC313D' }}>{t('wisdom-lamp.scheduleTitleRed')}</span>
            </h2>
          </div>

          {/* 時程內容 */}
          <div style={{ ...LINE, paddingLeft: 26, paddingTop: 24, paddingBottom: 16 }}>

            {/* Tab 切換 — bottom-line 風格 */}
            <div className="flex mb-4" style={{ alignSelf: 'stretch' }}>
              {[{ key: 'time', label: t('wisdom-lamp.tabByTime') }, { key: 'region', label: t('wisdom-lamp.tabByRegion') }].map(({ key, label }) => {
                const isOn = scheduleTab === key;
                return (
                  <button
                    key={key}
                    onClick={() => setScheduleTab(key)}
                    className="flex-1 pb-2 text-center font-sans transition-all duration-200"
                    style={{
                      fontSize: 18,
                      fontWeight: 500,
                      color: isOn ? '#BC313D' : 'rgba(188,49,61,0.5)',
                      borderBottom: isOn ? '4px solid #BC313D' : '4px solid rgba(188,49,61,0.5)',
                      background: 'none',
                      borderRadius: '8px 8px 0 0',
                      paddingLeft: 8,
                      paddingRight: 8,
                      paddingBottom: 8,
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {/* 依時間 */}
            {scheduleTab === 'time' && (
              <>
                {/* 月份 Chips */}
                {availableMonths.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {availableMonths.map((month) => {
                      const isOn = selectedMonth === month;
                      return (
                        <button
                          key={month}
                          onClick={() => setSelectedMonth(isOn ? null : month)}
                          className="font-sans"
                          style={{
                            paddingTop: 4,
                            paddingBottom: 5,
                            paddingLeft: 8,
                            paddingRight: 8,
                            borderRadius: 8,
                            outline: '1px solid #BC313D',
                            outlineOffset: -1,
                            background: isOn ? '#BC313D' : 'transparent',
                            fontSize: 18,
                            fontWeight: isOn ? 500 : 300,
                            color: isOn ? '#fff' : '#BC313D',
                            lineHeight: '27px',
                          }}
                        >
                          {isEn ? new Date(2000, month - 1).toLocaleString('en', { month: 'short' }) : `${month}${t('wisdom-lamp.monthSuffix')}`}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* 列表 */}
                <div className="space-y-3" style={{ marginLeft: -6 }}>
                  {(showAllSchedule || selectedMonth ? filteredByMonth : closestThree).map((item) => {
                    const startDate = item.date.split(' - ')[0];
                    return (
                      <div
                        key={item.lighting_id}
                        id={`schedule-${item.lighting_id}`}
                        className="rounded-[15px] px-4 py-3 shadow-sm"
                        style={{ backgroundColor: item.status === 'past' ? 'var(--color-background-footer)' : '#fff' }}
                      >
                        <p
                          className={`mb-1 ${item.status === 'current' ? 'font-bold' : 'font-normal'}`}
                          style={{
                            fontSize: 20,
                            color: item.status === 'past' ? 'var(--color-text-sub)' : '#BC313D',
                          }}
                        >
                          {startDate}
                        </p>
                        {isDesktop ? (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6 }}>
                            {(item.monasteryList || []).map((m) => {
                              const isDisabled = !m.has_lighting_post;
                              return (
                                <div key={m.id} onClick={() => setMonasteryPopup({ ...m, ...findMonasteryDetail(m.id), start_at: item.start_at })} style={{ paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2, background: isDisabled ? '#a0a0a05e' : 'rgba(0,70,130,0.75)', boxShadow: '0px 0px 4px 2px rgba(188,210,228,0.70) inset', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                  <span className={`font-serif ${isEn ? 'text-[15px]' : 'text-[16px] min-[400px]:text-[20px]'}`} style={{ textAlign: 'center', lineHeight: isEn ? '26px' : '30px', letterSpacing: isEn ? '-0.02em' : undefined, color: isDisabled ? '#1b1a1e96' : '#fff', fontWeight: 500 }}>
                                    {m.name}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 6 }}>
                            {(item.monasteryList || []).map((m) => {
                                  const isDisabled = !m.has_lighting_post;
                                  return (
                                    <div key={m.id} onClick={() => setMonasteryPopup({ ...m, ...findMonasteryDetail(m.id), start_at: item.start_at })} style={{ paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2, background: isDisabled ? '#a0a0a05e' : 'rgba(0,70,130,0.75)', boxShadow: '0px 0px 4px 2px rgba(188,210,228,0.70) inset', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                      <span className={`font-serif ${isEn ? 'text-[15px]' : 'text-[16px] min-[400px]:text-[20px]'}`} style={{ textAlign: 'center', lineHeight: isEn ? '26px' : '30px', letterSpacing: isEn ? '-0.02em' : undefined, color: isDisabled ? '#1b1a1e96' : '#fff', fontWeight: 500 }}>
                                          {m.name}
                                      </span>
                                    </div>
                                  );
                                })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {/* 展開 / 收合 按鈕（沒有選月份時才顯示） */}
                {!selectedMonth && scheduleItems.length > 3 && (
                  <button
                    onClick={() => setShowAllSchedule((v) => !v)}
                    className="mt-3 w-full py-2 rounded-full text-sm transition-all duration-200"
                    style={{ color: '#BC313D', border: '1px solid #BC313D', backgroundColor: 'transparent' }}
                  >
                    {showAllSchedule ? t('wisdom-lamp.collapse') : t('wisdom-lamp.expandAll').replace('{{count}}', scheduleItems.length)}
                  </button>
                )}
              </>
            )}

            {/* 依地區 */}
            {scheduleTab === 'region' && (() => {
              const flatMonasteries = (children) => {
                const result = [];
                (children || []).forEach((child) => {
                  if (child.type === 'monastery') result.push(child);
                  else if (child.children) result.push(...flatMonasteries(child.children));
                });
                return result;
              };
              const filteredRegions = selectedRegion
                ? branchRegions.filter((r) => r.name === selectedRegion)
                : branchRegions;
              return (
                <>
                  {/* 地區 Chips */}
                  {branchRegions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-5">
                      {branchRegions.map((region) => {
                        const isOn = selectedRegion === region.name;
                        return (
                          <button
                            key={region.name}
                            onClick={() => setSelectedRegion(isOn ? null : region.name)}
                            className="font-sans"
                            style={{
                              paddingTop: 4,
                              paddingBottom: 5,
                              paddingLeft: 8,
                              paddingRight: 8,
                              borderRadius: 8,
                              outline: '1px solid #BC313D',
                              outlineOffset: -1,
                              background: isOn ? '#BC313D' : 'transparent',
                              fontSize: 18,
                              fontWeight: isOn ? 500 : 300,
                              color: isOn ? '#fff' : '#BC313D',
                              lineHeight: '27px',
                            }}
                          >
                            {region.name}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* 地區列表 */}
                  <div style={{ paddingTop: 16, paddingBottom: 16, display: 'flex', flexDirection: 'column', gap: 0, marginLeft: -6 }}>
                    {filteredRegions.map((region, idx) => {
                      const monasteries = flatMonasteries(region.children);
                      const hasSubRegions = region.children?.some((c) => c.type === 'sub_region');
                      const MonasteryTag = ({ m }) => {
                        const isDisabled = !m.has_lighting_post;
                        return (
                          <div onClick={() => setMonasteryPopup(m)} style={{ paddingLeft: 6, paddingRight: 6, paddingTop: 2, paddingBottom: 2, background: isDisabled ? '#a0a0a05e' : 'rgba(0,70,130,0.75)', boxShadow: '0px 0px 4px 2px rgba(188,210,228,0.70) inset', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', minHeight: 60, width: '100%', height: '100%' }}>
                            <span className={`font-serif ${isEn ? 'text-[15px]' : 'text-[16px] min-[400px]:text-[20px]'}`} style={{ textAlign: 'center', color: isDisabled ? '#1b1a1e96' : '#fff', fontWeight: 500, lineHeight: isEn ? '26px' : '30px', letterSpacing: isEn ? '-0.02em' : undefined }}>
                              {m.name}
                            </span>
                          </div>
                        );
                      };
                      const MonasteryTags = ({ list }) => isDesktop ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                          {list.map((m) => (
                            <MonasteryTag key={m.id} m={m} />
                          ))}
                        </div>
                      ) : (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                          {list.map((m) => (
                            <MonasteryTag key={m.id} m={m} />
                          ))}
                          {list.length % 2 === 1 && <div />}
                        </div>
                      );
                      return (
                        <div key={region.name}>
                          {hasSubRegions ? (
                            <div style={{ paddingBottom: 12, display: 'flex', flexDirection: 'column', gap: 16 }}>
                              {region.children.map((sub) => (
                                <div key={sub.name}>
                                  <div style={{ fontSize: 22, color: '#CF4551', fontFamily: 'Noto Serif TC', marginBottom: 8 }}>{sub.name}</div>
                                  <MonasteryTags list={flatMonasteries(sub.children)} />
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{ paddingBottom: 12 }}>
                              <MonasteryTags list={monasteries} />
                            </div>
                          )}
                          {idx < filteredRegions.length - 1 && (
                            <div style={{ height: 0, outline: '1px solid rgba(110,98,91,0.30)', outlineOffset: -0.5 }} />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </div>

          {behindScenes.length === 0 && <div className="h-6" style={LINE_PL} />}
        </section>

        {/* ── Section 5：活動花絮（空則不顯示） ── */}
        {behindScenes.length > 0 && (
          <section className="px-6 pb-10 relative">
            <div className="relative" style={{ zIndex: 1 }}>
              <div className="h-6" style={LINE_PL} />

              <div style={ACCENT}>
                <h2 style={{ fontFamily: 'Noto Serif TC', fontSize: 30, fontWeight: 700, lineHeight: '33px' }}>
                  <span style={{ color: '#171717' }}>{t('wisdom-lamp.highlightsTitleBlack')}</span><br />
                  <span style={{ color: '#BC313D' }}>{t('wisdom-lamp.highlightsTitleRed')}</span>
                </h2>
              </div>

              <div style={{ ...LINE, paddingLeft: 26, paddingTop: 24, paddingBottom: 16 }}>
                <div className="overflow-x-auto no-scrollbar" style={{ display: 'flex', gap: 12, marginRight: -24, paddingRight: 24 }}>
                  {behindScenes.map((scene) => {
                    const isYT = scene.media_url && isYouTubeUrl(scene.media_url);
                    const thumb = isYT ? getYouTubeThumbnail(scene.media_url) : null;
                    return (
                      <div
                        key={scene.behind_scenes_id}
                        className="cursor-pointer group"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, width: 155, flexShrink: 0 }}
                        onClick={() => scene.media_url && setSelectedScene(scene)}
                      >
                        <div style={{ width: 155, aspectRatio: '1', position: 'relative', background: '#FAF7F3', boxShadow: '2px 4px 8px rgba(110,98,91,0.08)', borderRadius: 16, overflow: 'hidden' }}>
                          {thumb ? (
                            <img src={thumb} alt={scene.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : scene.media_url ? (
                            <video src={`${scene.media_url}#t=0.1`} preload="metadata" muted playsInline controlsList="nodownload" onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(188,49,61,0.08)' }}>
                              <span style={{ fontFamily: 'Noto Serif TC', fontSize: 20, fontWeight: 700, color: 'rgba(188,49,61,0.35)' }}>花</span>
                            </div>
                          )}
                          {scene.media_url && (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.15)' }}>
                              <span className="material-symbols-outlined" style={{ color: 'rgba(255,255,255,0.85)', fontSize: 32 }}>play_arrow</span>
                            </div>
                          )}
                        </div>
                        <p className="line-clamp-2 text-center" style={{ fontFamily: 'Noto Serif TC', fontSize: 14, fontWeight: 700, color: '#6E625B', letterSpacing: '0.1em', width: '100%' }}>
                          {scene.title}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="h-6" style={LINE_PL} />
            </div>
          </section>
        )}

      </div>

      {/* ── 影片 Modal ── */}
      {videoModal && mapItems[activeMapIndex]?.map_url && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={handleCloseVideo}
        >
          <CloseButton btnColor="text-white" onClick={handleCloseVideo} />
          <div
            className="relative w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              <video
                key={mapItems[activeMapIndex].map_url}
                src={mapItems[activeMapIndex].map_url}
                controls
                autoPlay
                playsInline
                controlsList="nodownload"
                onContextMenu={(e) => e.preventDefault()}
                className="absolute inset-0 w-full h-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── 精舍 Monastery Popup ── */}
      {monasteryPopup && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setMonasteryPopup(null)}
        >
          <div
            className="relative w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden"
            style={{ backgroundColor: '#FEFEFE' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* header */}
            <div className="px-6 pt-5 pb-4 flex items-center justify-between">
              <p style={{ fontFamily: 'Noto Serif TC', fontSize: 22, fontWeight: 700, color: monasteryPopup.has_lighting_post ? '#004682' : '#6E625B' }}>
                {monasteryPopup.name}
              </p>
              <CloseButton btnColor="text-[#6E625B]" onClick={() => setMonasteryPopup(null)} />
            </div>
            <div style={{ height: 1, backgroundColor: 'rgba(110,98,91,0.15)', marginLeft: 24, marginRight: 24 }} />
            {/* info */}
            <div className="px-6 pt-4 pb-5" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {(monasteryPopup.start_at || monasteryPopup.end_at) && (
                <div className="flex items-center gap-3 rounded-xl px-3 py-2" style={{ backgroundColor: 'rgba(0,70,130,0.07)' }}>
                  <span className="material-symbols-outlined text-lg" style={{ color: '#004682', flexShrink: 0 }}>calendar_month</span>
                  <span style={{ fontFamily: 'Noto Sans TC', fontSize: 15, color: '#004682', lineHeight: '22px' }}>
                    {(() => {
                      const fmt = (s) => { const d = new Date(s); return `${d.getMonth() + 1}/${String(d.getDate()).padStart(2, '0')}`; };
                      if (monasteryPopup.start_at && monasteryPopup.end_at) return `${fmt(monasteryPopup.start_at)} - ${fmt(monasteryPopup.end_at)}`;
                      if (monasteryPopup.start_at) return `${fmt(monasteryPopup.start_at)} 起`;
                      return '';
                    })()}
                  </span>
                </div>
              )}
              {monasteryPopup.address && (
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(monasteryPopup.name + ' ' + monasteryPopup.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 rounded-xl px-3 py-2 transition-colors"
                  style={{ backgroundColor: 'rgba(110,98,91,0.06)', textDecoration: 'none' }}
                >
                  <span className="material-symbols-outlined text-lg" style={{ color: '#BC313D', marginTop: 1, flexShrink: 0 }}>location_on</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span style={{ fontFamily: 'Noto Sans TC', fontSize: 15, color: '#6E625B', lineHeight: '22px' }}>
                      {monasteryPopup.address}
                    </span>
                    <span style={{ fontFamily: 'Noto Sans TC', fontSize: 12, color: 'rgba(110,98,91,0.45)', lineHeight: '16px' }}>
                      {t('wisdom-lamp.openMapHint')}
                    </span>
                  </div>
                </a>
              )}
              {monasteryPopup.has_lighting_post && (
                <button
                  onClick={() => { setMonasteryPopup(null); navigate(ROUTES.BRANCH_REPORT_DETAIL(monasteryPopup.id)); }}
                  className="w-full mt-1 py-2 rounded-full flex items-center justify-center gap-2 transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#004682', color: '#fff' }}
                >
                  <span style={{ fontFamily: 'Noto Sans TC', fontSize: 15, fontWeight: 500 }}>{t('wisdom-lamp.goToReport')}</span>
                  <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── 花絮 Modal ── */}
      {selectedScene?.media_url && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setSelectedScene(null)}
        >
          <button
            onClick={() => setSelectedScene(null)}
            className="fixed top-4 right-4 z-[10000] w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full border-2 border-white/30 text-white transition-all"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              {isYouTubeUrl(selectedScene.media_url) ? (
                <iframe
                  src={`${getYouTubeEmbedUrl(selectedScene.media_url)}?autoplay=1&rel=0&modestbranding=1`}
                  title={selectedScene.title}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <video
                  src={selectedScene.media_url}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full"
                />
              )}
            </div>
            {selectedScene.monastery_id && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => navigate(ROUTES.BRANCH_REPORT_DETAIL(selectedScene.monastery_id))}
                  className="flex items-center gap-2 px-6 py-2 rounded-full text-white transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#004682', fontFamily: 'Noto Sans TC', fontSize: 15, fontWeight: 500 }}
                >
                  {t('wisdom-lamp.goToMonastery')}
                  <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── 影音報導 Modal ── */}
      {selectedPost?.video?.[0]?.cache_url && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-sm p-4"
          onClick={() => setSelectedPost(null)}
        >
          <button
            onClick={() => setSelectedPost(null)}
            className="fixed top-4 right-4 z-[10000] w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 backdrop-blur-md rounded-full border-2 border-white/30 text-white transition-all"
          >
            <span className="material-symbols-outlined text-2xl">close</span>
          </button>
          <div className="relative w-full max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="relative aspect-video bg-black rounded-2xl overflow-hidden shadow-2xl">
              {isYouTubeUrl(selectedPost.video[0].cache_url) ? (
                <iframe
                  src={`${getYouTubeEmbedUrl(selectedPost.video[0].cache_url)}?autoplay=1&rel=0&modestbranding=1`}
                  title={selectedPost.monastery_name}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 w-full h-full border-0"
                />
              ) : (
                <video
                  src={selectedPost.video[0].cache_url}
                  controls
                  autoPlay
                  playsInline
                  controlsList="nodownload"
                  onContextMenu={(e) => e.preventDefault()}
                  className="absolute inset-0 w-full h-full"
                />
              )}
            </div>
            {selectedPost.monastery_id && (
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => navigate(ROUTES.BRANCH_REPORT_DETAIL(selectedPost.monastery_id))}
                  className="flex items-center gap-2 px-6 py-2 rounded-full text-white transition-opacity hover:opacity-80"
                  style={{ backgroundColor: '#004682', fontFamily: 'Noto Sans TC', fontSize: 15, fontWeight: 500 }}
                >
                  {t('wisdom-lamp.goToReport')}
                  <span className="material-symbols-outlined text-lg">arrow_forward</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WisdomLampPage;
